import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import founderApi, { errMsg } from "@/lib/founderApi";

const PLANS = [
  { id: "starter", name: "Starter", price: "$149/mo", note: "Up to 5 vehicles" },
  { id: "growth", name: "Growth", price: "$299/mo", note: "Up to 20 vehicles" },
  { id: "fleet", name: "Fleet", price: "$599/mo", note: "Up to 75 vehicles" },
  { id: "enterprise", name: "Enterprise", price: "Custom", note: "Unlimited" },
];

export function PlanDialog({ company, billing, open, onOpenChange, onDone }) {
  const [plan, setPlan] = useState(company.plan);
  const [mode, setMode] = useState("access");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPlan(company.plan); setMode("access"); } }, [open, company.plan]);
  const stripeOk = billing?.has_stripe && plan !== "enterprise";

  const save = async () => {
    setBusy(true);
    try {
      const r = await founderApi.post(`/companies/${company.id}/plan`, { plan, billing: mode });
      toast.success(`${company.name} is now on ${plan}. ${r.data.message}`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(errMsg(e, "Couldn't change the plan.")); } finally { setBusy(false); }
  };

  const Opt = ({ id, title, desc, disabled }) => (
    <label className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${mode === id ? "border-blue-500 bg-blue-600/10" : "border-slate-700"} ${disabled ? "cursor-not-allowed opacity-50" : ""}`} data-testid={`plan-billing-${id}`}>
      <input type="radio" checked={mode === id} disabled={disabled} onChange={() => setMode(id)} className="mt-1 accent-blue-600" />
      <span><span className="block text-sm font-semibold text-white">{title}</span><span className="text-xs text-slate-400">{desc}</span></span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-[#0f172a] text-white sm:max-w-lg" data-testid="plan-dialog">
        <DialogHeader>
          <DialogTitle>Change plan · {company.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Currently on {company.plan}. Use this for payment issues or special deals.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {PLANS.map((p) => (
            <button key={p.id} onClick={() => setPlan(p.id)} data-testid={`plan-option-${p.id}`}
              className={`rounded-lg border p-3 text-left transition-colors ${plan === p.id ? "border-blue-500 bg-blue-600/10" : "border-slate-700 hover:bg-slate-800"}`}>
              <p className="text-sm font-semibold">{p.name}{p.id === company.plan && <span className="ml-1.5 text-[10px] text-slate-400">(current)</span>}</p>
              <p className="text-xs text-slate-400">{p.price} · {p.note}</p>
            </button>
          ))}
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Billing</p>
          <Opt id="access" title="Change access only" desc="The company gets this plan's features right away. Stripe keeps charging what it charges today." />
          <Opt id="stripe" disabled={!stripeOk} title="Change access and Stripe billing"
            desc={stripeOk ? "Stripe switches them to the new price from today, prorated." : plan === "enterprise" ? "Enterprise is custom-priced — invoice it yourself." : "Not available — this company has no Stripe subscription yet."} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={() => onOpenChange(false)} className="rounded-md px-4 py-2 text-sm text-slate-300 hover:bg-slate-800" data-testid="plan-cancel">Cancel</button>
          <button onClick={save} disabled={busy || (plan === company.plan && mode === "access" && !billing?.manual_plan)} data-testid="plan-save"
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Switch to {plan}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function WinbackDialog({ company, open, onOpenChange, kind = "" }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { return; }
    setDraft(null);
    founderApi.get(`/platform/winback/${company.id}${kind ? `?kind=${kind}` : ""}`).then((r) => setDraft(r.data)).catch((e) => { toast.error(errMsg(e, "Couldn't prepare the email.")); onOpenChange(false); });
  }, [open, company.id, kind, onOpenChange]);

  const send = async () => {
    setBusy(true);
    try {
      await founderApi.post(`/platform/winback/${company.id}?kind=${draft.kind || kind || ""}`, { subject: draft.subject, message: draft.message });
      toast.success(`Email sent to ${draft.to}.`); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e, "Couldn't send the email.")); } finally { setBusy(false); }
  };
  const f = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-[#0f172a] text-white sm:max-w-xl" data-testid="winback-dialog">
        <DialogHeader>
          <DialogTitle>Email {company.owner?.name || "the owner"}</DialogTitle>
          <DialogDescription className="text-slate-400">{draft ? `From ${draft.from} to ${draft.to}. Edit anything before sending.` : "Preparing a draft…"}</DialogDescription>
        </DialogHeader>
        {!draft ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-400" /> : (
          <div className="space-y-3">
            <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className={f} data-testid="winback-subject" />
            <textarea rows={10} value={draft.message} onChange={(e) => setDraft({ ...draft, message: e.target.value })} className={f} data-testid="winback-message" />
            <div className="flex justify-end gap-2">
              <button onClick={() => onOpenChange(false)} className="rounded-md px-4 py-2 text-sm text-slate-300 hover:bg-slate-800" data-testid="winback-cancel">Cancel</button>
              <button onClick={send} disabled={busy} data-testid="winback-send" className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold hover:bg-blue-700">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send email
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
